<template>
  <div class="py-4 container-fluid">
    <!-- Back Button and Customer Info -->
    <div class="mb-3 d-flex align-items-center">
      <button class="btn btn-secondary me-3" @click="goBack">
        <i class="fas fa-arrow-left"></i> Zurück zur Kundenliste
      </button>
      <h5 class="mb-0" v-if="customerInfo">
        Call Logs für: <strong>{{ customerInfo.name }}</strong> ({{ customerInfo.email }})
      </h5>
    </div>

    <!-- Notification Section -->
    <div 
      class="alert alert-info alert-dismissible fade show" 
      role="alert" 
      v-if="showCallLogNotice"
    >
      ⚠️ Call logs are automatically deleted after 30 days.
      <button 
        type="button" 
        class="close" 
        aria-label="Close" 
        @click="showCallLogNotice = false"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="mt-4 row">
      <div class="col-12">
        <div class="card">
          <div class="card-header border-bottom">
            <div class="user d-flex align-items-center">
              <div class="col-6">
                <h5 class="mb-0">Calls Dashboard (Admin View)</h5>
              </div>
              <div class="col-6 d-flex justify-content-end">
                <div class="input-group">
                  <input
                    v-model="searchTerm"
                    type="text"
                    class="form-control"
                    placeholder="Search"
                  />
                </div>
                <div class="input-group ml-3">
                  <span class="input-group-text"></span>
                  <input 
                    type="date" 
                    v-model="selectedDate" 
                    class="form-control datepicker" 
                    @change="filterCalls" 
                    placeholder="Select Date"
                  />
                  <button 
                    v-if="selectedDate" 
                    class="btn btn-outline-secondary" 
                    type="button" 
                    @click="clearDate" 
                    title="Clear Date"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div><br>
            <div class="text-end">
              <button class="btn btn-sm btn-secondary ml-2" @click="downloadCallLog">
                <i class="fas fa-download"></i> Download Call Log
              </button>
            </div>
            <!-- Dropdown to control number of entries -->
            <label for="pageSizeSelect">Show: </label>
            <select id="pageSizeSelect" v-model="pageSize" @change="onPageSizeChange">
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="All">All</option>
            </select>
            entries
          </div>

          <div v-if="loading" class="text-center my-4">
            <i class="fas fa-spinner fa-spin"></i> Loading calls...
          </div>

          <div v-if="error" class="alert alert-danger m-3">
            {{ error }}
          </div>

          <div class="table-responsive" v-if="!loading && !error">
            <table class="table table-striped">
              <thead class="thead-light">
                <tr>
                  <th class="align-middle">Phonebot</th>
                  <th class="align-middle">Category</th>
                  <th class="align-middle">
                    Priority
                    <button 
                      class="btn btn-md btn-link sort-button" 
                      @click="toggleSortBy('priority')"
                    >
                      <i class="fas" :class="sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'"></i>
                    </button>
                  </th>
                  <th class="align-middle">Phone Number</th>
                  <th class="align-middle">Message Preview</th>
                  <th class="align-middle">
                    Status
                    <button 
                      class="btn btn-md btn-link sort-button" 
                      @click="toggleSortBy('status')"
                    >
                      <i class="fas" :class="sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'"></i>
                    </button>
                  </th>
                  <th class="align-middle">
                    Date/Time
                    <button 
                      class="btn btn-md btn-link sort-button" 
                      @click="toggleSortBy('created_at')"
                    >
                      <i class="fas" :class="sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'"></i>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredCalls.length === 0">
                  <td colspan="7" class="text-center">No calls found.</td>
                </tr>
                <tr v-for="call in paginatedCalls" :key="call.id">
                  <td class="align-middle">{{ call.bezeichnung }}</td>
                  <td class="align-middle">{{ call.selected_category || 'Uncategorized' }}</td>
                  <td class="align-middle">
                    <span :class="getPriorityBadgeClass(call.priority)">
                      {{ call.priority }}
                    </span>
                  </td>
                  <td class="align-middle">{{ call.caller_number }}</td>
                  <td class="align-middle">
                    <span 
                      class="text-truncate" 
                      style="text-decoration: underline; cursor: pointer;" 
                      @click="showFullText(call)"
                    >
                      {{ formatText(call.response) }}.....more
                    </span>
                  </td>
                  <td class="align-middle">
                    <span :class="getStatusBadgeClass(call.status)">
                      {{ call.status }}
                    </span>
                  </td>
                  <td class="align-middle">{{ formatDate(call.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="d-flex justify-content-end mt-4">
            <nav aria-label="Page navigation">
              <ul class="pagination">
                <li class="page-item" :class="{'disabled': currentPage === 1}">
                  <button class="page-link" @click="changePage(currentPage - 1)">
                    <i class="fas fa-chevron-left"></i>
                  </button>
                </li>
                <li 
                  v-for="page in totalPages" 
                  :key="page" 
                  class="page-item" 
                  :class="{'active': currentPage === page}"
                >
                  <button class="page-link" @click="changePage(page)">{{ page }}</button>
                </li>
                <li class="page-item" :class="{'disabled': currentPage === totalPages}">
                  <button class="page-link" @click="changePage(currentPage + 1)">
                    <i class="fas fa-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for showing full text -->
    <div v-if="showModal" class="modal fade show" tabindex="-1" style="display: block;" aria-hidden="false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Message Preview</h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body" style="max-height: 400px; overflow-y: auto;">
            <div class="chat-box p-3 border rounded bg-light">
              <div v-for="(pair, index) in fullText" :key="index" class="mb-2">
                <span v-if="pair.action === 'UserRecognizedText'" class="d-block">
                  <strong>Caller:</strong> {{ pair.text }}
                </span>
                <span v-else-if="pair.action === 'BotResponseText'" class="d-block">
                  <strong>Bot:</strong> {{ pair.text }}
                </span>
                <span v-else class="d-block">
                  <strong>Action:</strong> {{ pair.text }}
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import userService from "@/services/users.service";

export default {
  name: 'AdminCallLogs',
  data() {
    return {
      calls: [],
      customerInfo: null,
      customerId: null,
      searchTerm: '',
      currentPage: 1,
      pageSize: 20,
      showModal: false,
      fullText: '',
      loading: true,
      error: null,
      sortOrder: 'desc',
      sortBy: 'created_at',
      selectedDate: '',
      showCallLogNotice: true,
    };
  },

  async mounted() {
    // Decode customer info from route query params
    this.decodeCustomerInfo();
    if (this.customerId) {
      await this.fetchCalls();
    }
  },

  methods: {
    decodeCustomerInfo() {
      try {
        // Lese die Daten aus den Query-Parametern
        const { userId, name, email } = this.$route.query;
        
        console.log('Query params:', this.$route.query);
        
        if (userId) {
          this.customerId = parseInt(userId);
          this.customerInfo = {
            name: name || 'Unknown',
            email: email || ''
          };
          
          console.log('Customer ID:', this.customerId);
          console.log('Customer Info:', this.customerInfo);
        } else {
          this.error = 'No customer ID provided';
          setTimeout(() => {
            this.$router.push({ name: 'customers' });
          }, 2000);
        }
      } catch (error) {
        console.error('Error decoding customer info:', error);
        this.error = 'Failed to decode customer information';
        this.$router.push({ name: 'customers' });
      }
    },

    async fetchCalls() {
      try {
        this.loading = true;
        this.error = null;
        
        console.log('Fetching calls for customer ID:', this.customerId);
        
        // Sende die Customer-ID über den Header
        const response = await userService.getCallsByCustomerId(this.customerId);
        
        console.log('API Response:', response);
        
        this.calls = response.call_logs || [];
        
        // Update customer info from response if available
        if (response.customer) {
          this.customerInfo = {
            name: response.customer.name || this.customerInfo.name,
            email: response.customer.email || this.customerInfo.email
          };
        }
        
        this.sortCalls();
      } catch (error) {
        console.error("Error fetching calls data:", error);
        this.error = 'Failed to load call logs: ' + (error.response?.data?.error || error.message);
      } finally {
        this.loading = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'customers' });
    },

    toggleSortBy(property) {
      if (this.sortBy === property) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = property;
        this.sortOrder = 'desc';
      }
      this.sortCalls();
    },

    sortCalls() {
      this.calls.sort((a, b) => {
        let valueA, valueB;

        if (this.sortBy === 'created_at') {
          valueA = new Date(a.created_at);
          valueB = new Date(b.created_at);
        } else if (this.sortBy === 'priority') {
          const priorityOrder = ['Normal Callback', 'Important Callback', 'Send Email', 'Transferred'];
          valueA = priorityOrder.indexOf(a.priority);
          valueB = priorityOrder.indexOf(b.priority);
        } else if (this.sortBy === 'status') {
          const statusOrder = ['New', 'In Progress', 'Completed'];
          valueA = statusOrder.indexOf(a.status);
          valueB = statusOrder.indexOf(b.status);
        }

        if (this.sortOrder === 'asc') {
          return valueA - valueB;
        } else {
          return valueB - valueA;
        }
      });
    },

    getPriorityBadgeClass(priority) {
      switch (priority) {
        case 'Normal Callback':
          return 'badge bg-warning text-dark';
        case 'Important Callback':
          return 'badge bg-danger';
        case 'Send Email':
          return 'badge bg-success';
        case 'Transferred':
          return 'badge bg-info';
        default:
          return 'badge bg-secondary';
      }
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'New':
          return 'badge bg-primary';
        case 'In Progress':
          return 'badge bg-warning text-dark';
        case 'Completed':
          return 'badge bg-success';
        default:
          return 'badge bg-secondary';
      }
    },

    formatText(response) {
      try {
        const parsedResponse = Array.isArray(response) ? response : JSON.parse(response);
        if (!Array.isArray(parsedResponse) || parsedResponse.length === 0) return '';
        
        const firstText = parsedResponse[0]?.text || '';
        return firstText.length > 20 ? firstText.substring(0, 20) + '...' : firstText;
      } catch (error) {
        console.error('Error parsing response:', error);
        return '';
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString('en-GB', { month: 'short' });
      const hour = date.getHours();
      const minute = date.getMinutes().toString().padStart(2, '0');
      return `${day}. ${month}, ${hour}:${minute}`;
    },

    showFullText(call) {
      if (typeof call.response === 'string') {
        try {
          call.response = JSON.parse(call.response);
        } catch (error) {
          console.error('Failed to parse response:', error);
          call.response = [];
        }
      }

      if (Array.isArray(call.response) && call.response.length > 0) {
        this.fullText = call.response;
      } else {
        this.fullText = [{ text: "No valid responses", action: "None" }];
      }

      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.fullText = '';
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    filterCalls() {
      this.currentPage = 1;
    },

    clearDate() {
      this.selectedDate = '';
      this.searchTerm = '';
    },

    onPageSizeChange() {
      this.currentPage = 1;
    },

    downloadCallLog() {
      const headers = ['PhoneBot', 'Category', 'Priority', 'Phone Number', 'Status', 'Message', 'Date/Time'];
      const rows = this.filteredCalls.map(call => [
        call.bezeichnung || '',
        call.selected_category || '',
        call.priority || '',
        call.caller_number || '',
        call.status || '',
        call.response || '',
        this.formatDate(call.created_at)
      ]);

      const csvContent = [headers, ...rows]
        .map(row => row.map(value => `"${value}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `call_log_${this.customerInfo?.name || 'customer'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },

  computed: {
    filteredCalls() {
      const lowerSearchTerm = this.searchTerm ? this.searchTerm.trim().toLowerCase() : '';
      const selectedDate = this.selectedDate ? this.selectedDate : '';

      if (!lowerSearchTerm && !selectedDate) {
        return this.calls;
      }

      return this.calls.filter(call => {
        const statusMatch = call.status && call.status.toLowerCase().includes(lowerSearchTerm);
        const priorityMatch = call.priority && call.priority.toLowerCase().includes(lowerSearchTerm);
        const phonebotNameMatch = call.bezeichnung && call.bezeichnung.toLowerCase().includes(lowerSearchTerm);
        const category = call.selected_category && call.selected_category.toLowerCase().includes(lowerSearchTerm);

        let dateMatch = true;
        if (selectedDate) {
          const callDate = new Date(call.created_at).toISOString().split('T')[0];
          dateMatch = callDate === selectedDate;
        }

        return (statusMatch || priorityMatch || phonebotNameMatch || category) && dateMatch;
      });
    },

    paginatedCalls() {
      if (this.pageSize === 'All') {
        return this.filteredCalls;
      }
      const startIndex = (this.currentPage - 1) * this.pageSize;
      return this.filteredCalls.slice(startIndex, startIndex + parseInt(this.pageSize));
    },

    totalPages() {
      if (this.pageSize === 'All') {
        return 1;
      }
      return Math.ceil(this.filteredCalls.length / this.pageSize);
    },
  },
};
</script>

<style scoped>
.table {
  width: 100%;
  border-collapse: collapse !important;
}

.table-striped th,
.table-striped td {
  padding: 8px;
  text-align: left;
}

.thead-light {
  background-color: #f8f9fa;
}

th, td {
  vertical-align: middle;
}

.pagination {
  margin: 0;
}

.page-item.disabled .page-link {
  pointer-events: none;
}

.page-link {
  border-radius: 50%;
  padding: 0.5rem;
}

.page-item.active .page-link {
  background-color: #007bff;
  color: white;
}

.input-group {
  width: 100%;
  max-width: 300px;
}

.badge {
  padding: 0.5em 0.75em;
  font-size: 0.85em;
}

.sort-button {
  padding: 0;
  margin-left: 5px;
}

.ml-3 {
  margin-left: 1rem;
}

.me-3 {
  margin-right: 1rem;
}

.form-control {
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  padding: 8px 12px;
}

.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.25rem rgba(38, 143, 255, 0.25);
}
</style>